import { useEffect, useState } from "react";

/** Matches the server's rule in users.ts and /auth/change-password. */
const MIN_PASSWORD_LENGTH = 6;
import { useToast } from "../components.js";
import { RolesEditor } from "./RolesEditor.js";
import type { AuditLog, User, UserRole } from "@zenforge/shared";



export function Users() {
  const [activeTab, setActiveTab] = useState<"users" | "roles" | "audit">("users");
  // Fetched rather than hardcoded: roles are rows since migration 045, so a
  // fixed list here would silently omit every custom role.
  const [roles, setRoles] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal State for New User
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<UserRole>("Content Reviewer");
  const [newUserPassword, setNewUserPassword] = useState("");
  // Whose password is being reset, and to what. Null closes the row's form.
  const [resettingUserId, setResettingUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const { toast, element: toastElement } = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Filter for Audit Logs
  const [actionFilter, setActionFilter] = useState<string>("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/v1/users");
      if (!res.ok) throw new Error("Failed to fetch users");
      const data: User[] = await res.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error loading users");
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const url = actionFilter
        ? `/api/v1/audit-logs?action=${encodeURIComponent(actionFilter)}`
        : "/api/v1/audit-logs";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch audit logs");
      const data: AuditLog[] = await res.json();
      setAuditLogs(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error loading audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "audit") {
      fetchAuditLogs();
    }
  }, [activeTab, actionFilter]);

  // Loaded once and kept across tabs: the assignable-role dropdowns live on
  // the Users tab but the list changes on the Roles tab beside it.
  const fetchRoles = async () => {
    try {
      const res = await fetch("/api/v1/roles");
      if (!res.ok) return;
      const data: { name: string }[] = await res.json();
      setRoles(data.map((r) => r.name));
    } catch {
      // Non-fatal: the dropdown falls back to empty and the page still lists
      // users. Surfacing an error here would bury the actual content.
    }
  };

  useEffect(() => {
    void fetchRoles();
  }, [activeTab]);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim()) return;
    if (newUserPassword.length < MIN_PASSWORD_LENGTH) {
      toast(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch("/api/v1/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newUserName.trim(),
          email: newUserEmail.trim(),
          role: newUserRole,
          password: newUserPassword,
        }),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || "Failed to create user");
      }

      setNewUserName("");
      setNewUserEmail("");
      setNewUserRole("Content Reviewer");
      setNewUserPassword("");
      setIsAddModalOpen(false);
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const res = await fetch(`/api/v1/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !user.isActive }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update user status");
    }
  };

  /**
   * An admin setting someone else's password — the "they forgot it" path.
   * Distinct from a person changing their own, which asks for the current one.
   */
  const handleSetPassword = async (user: User) => {
    try {
      const res = await fetch(`/api/v1/users/${user.id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: resetPassword }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Failed to set password");

      toast(`Password updated for ${user.name}`);
      setResettingUserId(null);
      setResetPassword("");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to set password");
    }
  };

  const handleRoleChange = async (user: User, role: UserRole) => {
    try {
      const res = await fetch(`/api/v1/users/${user.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error("Failed to update role");
      await fetchUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update user role");
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "Admin":
        return "badge approved";
      case "Content Reviewer":
        return "badge generated";
      case "Content Generator":
        return "badge active";
      case "Content Editor":
        return "badge pending";
      case "Knowledge Uploader":
        return "badge review";
      default:
        return "badge";
    }
  };

  return (
    <div className="section">
      <div className="row justify-between mb-4">
        <div className="row gap-2">
          <button
            className={`btn ${activeTab === "users" ? "primary" : "secondary"}`}
            onClick={() => setActiveTab("users")}
          >
            Team Members
          </button>
          <button
            className={`btn ${activeTab === "roles" ? "primary" : "secondary"}`}
            onClick={() => setActiveTab("roles")}
          >
            Roles & Permissions
          </button>
          <button
            className={`btn ${activeTab === "audit" ? "primary" : "secondary"}`}
            onClick={() => setActiveTab("audit")}
          >
            Action Audit Log
          </button>
        </div>

        {activeTab === "users" ? (
          <button className="btn primary" onClick={() => setIsAddModalOpen(true)}>
            + Add Team Member
          </button>
        ) : (
          <div className="row gap-2">
            <select
              className="select"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="USER_CREATED">USER_CREATED</option>
              <option value="USER_UPDATED">USER_UPDATED</option>
              <option value="USER_DEACTIVATED">USER_DEACTIVATED</option>
              <option value="CONTENT_REVIEWED">CONTENT_REVIEWED</option>
              <option value="CONTENT_APPROVED">CONTENT_APPROVED</option>
              <option value="CONTENT_REGENERATED">CONTENT_REGENERATED</option>
            </select>
            <button className="btn secondary" onClick={fetchAuditLogs}>
              Refresh Logs
            </button>
          </div>
        )}
      </div>

      {error && <div className="callout error mb-4">{error}</div>}

      {activeTab === "roles" ? (
        <RolesEditor toast={toast} />
      ) : loading ? (
        <div className="loading">Loading {activeTab}...</div>
      ) : activeTab === "users" ? (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>#{user.id}</td>
                  <td>
                    <strong>{user.name}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <select
                      className="select"
                      style={{ padding: "2px 6px", fontSize: "0.85rem" }}
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                    >
                      {roles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`badge ${user.isActive ? "approved" : "failed"}`}>
                      {user.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        className="btn secondary"
                        style={{ padding: "2px 8px", fontSize: "0.8rem" }}
                        onClick={() => handleToggleUserStatus(user)}
                      >
                        {user.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="btn secondary"
                        style={{ padding: "2px 8px", fontSize: "0.8rem" }}
                        onClick={() => {
                          setResettingUserId(resettingUserId === user.id ? null : user.id);
                          setResetPassword("");
                        }}
                      >
                        {resettingUserId === user.id ? "Cancel" : "Set password"}
                      </button>
                    </div>

                    {resettingUserId === user.id && (
                      <div className="row" style={{ gap: 6, marginTop: 6 }}>
                        <input
                          type="password"
                          className="input"
                          style={{ padding: "2px 6px", fontSize: "0.85rem", maxWidth: 190 }}
                          placeholder={`New password (${MIN_PASSWORD_LENGTH}+ chars)`}
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          autoFocus
                        />
                        <button
                          className="btn primary"
                          style={{ padding: "2px 8px", fontSize: "0.8rem" }}
                          disabled={resetPassword.length < MIN_PASSWORD_LENGTH}
                          onClick={() => void handleSetPassword(user)}
                        >
                          Save
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    No team members found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Timestamp</th>
                <th>User / Actor</th>
                <th>Action</th>
                <th>Target Entity</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map((log) => (
                <tr key={log.id}>
                  <td>#{log.id}</td>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>
                    <strong>{log.userName || "System"}</strong>
                    {log.userRole && (
                      <span className={`badge ${getRoleBadgeClass(log.userRole)} ml-2`}>
                        {log.userRole}
                      </span>
                    )}
                  </td>
                  <td>
                    <code>{log.action}</code>
                  </td>
                  <td>
                    {log.entityType ? `${log.entityType} #${log.entityId || ""}` : "—"}
                  </td>
                  <td>
                    <pre style={{ margin: 0, fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                      {log.details ? JSON.stringify(log.details) : "—"}
                    </pre>
                  </td>
                </tr>
              ))}
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No audit log entries recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Add Team Member</h3>
            <form onSubmit={handleAddUser}>
              <div className="form-group mb-3">
                <label>Full Name</label>
                <input
                  type="text"
                  className="input"
                  required
                  placeholder="e.g. John Doe"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>
              <div className="form-group mb-3">
                <label>Email Address</label>
                <input
                  type="email"
                  className="input"
                  required
                  placeholder="e.g. john@zenmarks.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>
              <div className="form-group mb-3">
                <label>Initial Password</label>
                <input
                  type="password"
                  className="input"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                />
                <div className="muted mini" style={{ marginTop: 4 }}>
                  Share it with them directly. They can change it from their own account.
                </div>
              </div>
              <div className="form-group mb-4">
                <label>Role</label>
                <select
                  className="select"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as UserRole)}
                >
                  {roles.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="row justify-end gap-2">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn primary" disabled={submitting}>
                  {submitting ? "Saving..." : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {toastElement}
    </div>
  );
}
